package fr.manyverse.app.presentation.components.modules.combat

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import fr.manyverse.app.domain.model.ActiveCombat
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.CombatParticipant

/**
 * Frise de l'ordre du combat affichée côté joueur (au clic sur la bulle).
 * Chaque carte montre le rang, l'avatar/icône et le nom ; le participant actif est mis en avant.
 */
@Composable
fun CombatFrieze(
    combat: ActiveCombat,
    characters: List<Character>,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier
) {
    // Retour arrière (Android, Échap sur desktop, swipe de bord sur iOS) : referme la frise,
    // comme le faisait le Dialog via le même dispatcher
    NavigationBackHandler(
        state = rememberNavigationEventState(NavigationEventInfo.None),
        onBackCompleted = onDismiss
    )

    // Le Dialog prenait le focus et fermait ainsi le clavier
    val focusManager = LocalFocusManager.current
    LaunchedEffect(Unit) { focusManager.clearFocus() }

    // Box plein écran dans l'arbre de l'écran, et non un Dialog : l'ordre d'affichage avec les
    // autres overlays suit l'ordre de déclaration dans PlayerScreen.
    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        // Scrim : un tap en dehors de la frise la referme. Frère (et non parent) de la carte,
        // pour qu'un tap sur la carte n'atteigne pas ce détecteur.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(MaterialTheme.colorScheme.scrim.copy(alpha = 0.6f))
                .pointerInput(Unit) { detectTapGestures { onDismiss() } }
        )

        Surface(
            shape = RoundedCornerShape(20.dp),
            color = MaterialTheme.colorScheme.surface,
            tonalElevation = 4.dp,
            modifier = modifier
                .safeDrawingPadding()
                .padding(16.dp)
        ) {
            LazyRow(
                contentPadding = PaddingValues(16.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                itemsIndexed(
                    items = combat.participants,
                    key = { _, participant -> participant.id }
                ) { index, participant ->
                    CombatFriezeCard(
                        order = index + 1,
                        participant = participant,
                        characters = characters,
                        isActive = index == combat.activeIndex
                    )
                }
            }
        }
    }
}

@Composable
private fun CombatFriezeCard(
    order: Int,
    participant: CombatParticipant,
    characters: List<Character>,
    isActive: Boolean
) {
    val borderColor = if (isActive) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.outlineVariant
    val containerColor = if (isActive) MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.4f)
    else MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.4f)

    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier
            .width(110.dp)
            .clip(RoundedCornerShape(16.dp))
            .background(containerColor)
            .border(if (isActive) 2.dp else 1.dp, borderColor, RoundedCornerShape(16.dp))
            .padding(vertical = 14.dp, horizontal = 8.dp)
    ) {
        OrderBadge(order = order, isActive = isActive)
        CombatParticipantAvatar(
            participant = participant,
            characters = characters,
            size = 56.dp
        )
        Text(
            text = participant.name,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = if (isActive) FontWeight.Bold else FontWeight.Normal,
            color = MaterialTheme.colorScheme.onSurface,
            textAlign = TextAlign.Center,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
internal fun OrderBadge(order: Int, isActive: Boolean) {
    Box(
        modifier = Modifier
            .size(28.dp)
            .clip(RoundedCornerShape(8.dp))
            .background(
                if (isActive) MaterialTheme.colorScheme.primary
                else MaterialTheme.colorScheme.surfaceVariant
            ),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = order.toString(),
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.Bold,
            color = if (isActive) MaterialTheme.colorScheme.onPrimary
            else MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}
